import { useAuthStore } from '@/features/auth/auth-store';

/**
 * Pending-access state for authenticated users with no admin-granted role
 * (SCRUM-387, v7 renderUnassignedState). Copy is DELIBERATELY VAGUE — it does
 * not confirm whether an account or role exists (security note from the
 * 2026-06-25 client call, preserved verbatim from the prototype).
 *
 * "Check again" re-runs role resolution: demo-era that is a re-render (the
 * demo store is authoritative, so the state persists — faithful to the
 * prototype's behavior); when SCRUM-102 Phase C lands, this becomes a claims
 * re-fetch.
 */
const CONTACT_MAILTO =
  'mailto:?subject=BankIQ%20access%20%E2%80%94%20role%20assignment%20needed&body=Hi%2C%20I%27ve%20signed%20into%20BankIQ%20but%20don%27t%20have%20a%20role%20assigned%20yet.%20Could%20you%20set%20up%20my%20access%3F';

export function UnassignedState() {
  const signOut = useAuthStore((s) => s.signOut);
  const setDemoRole = useAuthStore((s) => s.setDemoRole);

  return (
    <section
      aria-label="Access pending"
      className="flex min-h-full flex-col items-center justify-center p-8 text-center"
    >
      <svg
        viewBox="0 0 160 130"
        width="168"
        height="136"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="mb-4"
      >
        <circle
          cx="80"
          cy="62"
          r="46"
          stroke="var(--border-1)"
          strokeWidth="1.4"
          strokeDasharray="2 4"
          fill="none"
        />
        <rect x="58" y="34" width="44" height="58" rx="6" stroke="var(--text-3)" />
        <line x1="74" y1="30" x2="86" y2="30" stroke="var(--text-3)" strokeWidth="2.4" />
        <circle cx="80" cy="52" r="8" stroke="var(--text-3)" />
        <path d="M70 74c0-6 4.5-9 10-9s10 3 10 9" stroke="var(--text-3)" />
        <line x1="68" y1="84" x2="92" y2="84" stroke="var(--border-1)" strokeDasharray="3 3" />
      </svg>

      <span className="bg-bg-3 text-text-2 border-border-1 rounded-full border px-3 py-1 font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase">
        Access pending
      </span>
      <h1 className="font-display text-text-0 mt-4 text-xl font-bold">
        Your access isn&rsquo;t ready yet
      </h1>
      <p className="text-text-2 mt-2 max-w-[420px] text-sm">
        If your access has been set up, your workspace will appear automatically once configuration
        is complete. If you think this is a mistake, contact your administrator.
      </p>

      <div className="mt-6 flex items-center gap-3">
        <a
          href={CONTACT_MAILTO}
          className="bg-brand-primary text-bg-0 rounded-tk-md px-4 py-2 text-[0.8125rem] font-semibold"
        >
          Contact your administrator
        </a>
        <button
          type="button"
          onClick={() => {
            // Re-run role resolution (demo-era: re-applies the demo store state).
            setDemoRole('Unassigned');
          }}
          className="border-border-1 bg-bg-3 text-text-1 hover:text-text-0 rounded-tk-md border px-4 py-2 text-[0.8125rem] font-medium transition-colors"
        >
          Check again
        </button>
        <button
          type="button"
          onClick={signOut}
          className="text-text-3 hover:text-text-0 px-2 py-2 text-[0.8125rem] transition-colors"
        >
          Sign out
        </button>
      </div>
    </section>
  );
}
